// Comparison overlay payload — matches CompareSensory but is plot-only so the
// component doesn't take a workbench-aware dependency. Each entry renders one
// dashed polygon under the Current polygon, plus a legend swatch.
export type RadarComparison = {
  id: string;
  name: string;
  color: string;
  values: number[] | null;
};

type RadarPlotProps = {
  axes: string[];
  target: number[];
  current: number[];
  comparisons?: RadarComparison[];
  size?: number;
  label?: string;
  hovered?: number | null;
  onHover?: (i: number | null) => void;
};

export function RadarPlot({
  axes,
  target,
  current,
  comparisons = [],
  size = 280,
  label,
  hovered,
  onHover,
}: RadarPlotProps) {
  const n = axes.length;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 48;
  const max = 10;

  const angleFor = (i: number) => (i / n) * Math.PI * 2 - Math.PI / 2;
  const pointAt = (i: number, v: number): [number, number] => {
    const a = angleFor(i);
    const rr = (v / max) * r;
    return [cx + Math.cos(a) * rr, cy + Math.sin(a) * rr];
  };
  const polyPoints = (values: number[]) =>
    values.map((v, i) => pointAt(i, v).join(",")).join(" ");

  const rings = [0.25, 0.5, 0.75, 1.0];

  return (
    <div className="vstack" style={{ alignItems: "center", gap: 8 }}>
      <div
        className="hstack"
        style={{
          width: "100%",
          justifyContent: "space-between",
          padding: "0 4px",
        }}
      >
        {label && <div className="label">{label}</div>}
        <div
          className="hstack gap-3"
          style={{ flexWrap: "wrap", justifyContent: "flex-end", rowGap: 4 }}
        >
          <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
            <span
              style={{
                display: "inline-block",
                width: 10,
                height: 10,
                marginRight: 6,
                verticalAlign: "middle",
                border: "1.25px dashed var(--ink)",
                background: "transparent",
                borderRadius: 1,
              }}
            />
            Target
          </span>
          <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
            <span
              style={{
                display: "inline-block",
                width: 10,
                height: 10,
                marginRight: 6,
                verticalAlign: "middle",
                background: "var(--accent)",
                border: "1.5px solid var(--accent-ink)",
                borderRadius: 1,
              }}
            />
            Current
          </span>
          {comparisons.map((c) => (
            <span
              key={c.id}
              className="mono"
              style={{
                fontSize: 10,
                color: "var(--ink-4)",
                maxWidth: 110,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: 10,
                  height: 10,
                  marginRight: 6,
                  verticalAlign: "middle",
                  background: c.color,
                  opacity: 0.28,
                  border: `1.5px solid ${c.color}`,
                  borderRadius: 1,
                }}
              />
              {c.name}
            </span>
          ))}
        </div>
      </div>

      <svg width={size} height={size} style={{ overflow: "visible" }}>
        {/* Rings */}
        {rings.map((f, idx) => (
          <polygon
            key={idx}
            points={axes
              .map((_, i) => {
                const a = angleFor(i);
                return [cx + Math.cos(a) * r * f, cy + Math.sin(a) * r * f].join(
                  ","
                );
              })
              .join(" ")}
            fill={idx === rings.length - 1 ? "var(--bg-sunk)" : "none"}
            stroke="var(--line)"
            strokeWidth="0.5"
            strokeDasharray={idx === rings.length - 1 ? undefined : "2 2"}
          />
        ))}

        {/* Spokes */}
        {axes.map((_, i) => {
          const [x, y] = pointAt(i, max);
          return (
            <line
              key={i}
              x1={cx}
              y1={cy}
              x2={x}
              y2={y}
              stroke="var(--line)"
              strokeWidth="0.5"
            />
          );
        })}

        {/* Target polygon — dashed outline + ghost fill */}
        <polygon
          points={polyPoints(target)}
          fill="var(--ink)"
          fillOpacity="0.04"
          stroke="var(--ink)"
          strokeWidth="1.25"
          strokeDasharray="4 3"
        />

        {/* Target vertex pips */}
        {target.map((v, i) => {
          const [x, y] = pointAt(i, v);
          return (
            <circle
              key={`t${i}`}
              cx={x}
              cy={y}
              r="2.2"
              fill="var(--panel)"
              stroke="var(--ink)"
              strokeWidth="1"
            />
          );
        })}

        {/* Comparison polygons — drawn UNDER current so current always reads on top */}
        {comparisons.map((c) =>
          c.values ? (
            <g key={c.id}>
              <polygon
                points={polyPoints(c.values)}
                fill={c.color}
                fillOpacity="0.16"
                stroke={c.color}
                strokeWidth="1.4"
                strokeOpacity="0.9"
                strokeDasharray="3 3"
              />
              {c.values.map((v, i) => {
                const [x, y] = pointAt(i, v);
                return (
                  <circle
                    key={`${c.id}_${i}`}
                    cx={x}
                    cy={y}
                    r="2.2"
                    fill={c.color}
                    fillOpacity="0.85"
                  />
                );
              })}
            </g>
          ) : null
        )}

        {/* Current polygon — solid accent fill */}
        <polygon
          points={polyPoints(current)}
          fill="var(--accent)"
          fillOpacity="0.32"
          stroke="var(--accent-ink)"
          strokeWidth="1.6"
        />

        {/* Current vertices */}
        {current.map((v, i) => {
          const [x, y] = pointAt(i, v);
          return <circle key={i} cx={x} cy={y} r="2.6" fill="var(--accent-ink)" />;
        })}

        {/* Axis labels */}
        {axes.map((axis, i) => {
          const a = angleFor(i);
          const lx = cx + Math.cos(a) * (r + 18);
          const ly = cy + Math.sin(a) * (r + 18);
          const anchor =
            Math.abs(Math.cos(a)) < 0.3
              ? "middle"
              : Math.cos(a) > 0
              ? "start"
              : "end";
          const isHover = hovered === i;
          return (
            <g
              key={i}
              onMouseEnter={() => onHover && onHover(i)}
              onMouseLeave={() => onHover && onHover(null)}
            >
              <circle
                cx={cx + Math.cos(a) * r}
                cy={cy + Math.sin(a) * r}
                r="8"
                fill="transparent"
              />
              <text
                x={lx}
                y={ly}
                textAnchor={anchor}
                dominantBaseline="middle"
                fontSize={n > 12 ? 9 : 10}
                fontFamily="var(--font-mono)"
                fill={isHover ? "var(--ink)" : "var(--ink-3)"}
                style={{
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  fontWeight: isHover ? 600 : 400,
                }}
              >
                {axis}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
